import {Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AbstractControl, FormControl, ReactiveFormsModule} from '@angular/forms';
import {Subscription} from 'rxjs';
import {
  NgClearButtonTemplateDirective,
  NgMultiLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent
} from '@ng-select/ng-select';
import {BusinessCertificateOptionDto} from '../../../pages/user-business/add/business-api.service';
import {formatProductionSite, formatProductionSiteShort} from './production-site';

@Component({
  selector: 'app-certificate-select',
  imports: [NgSelectComponent, NgOptionTemplateDirective, NgMultiLabelTemplateDirective, NgClearButtonTemplateDirective, ReactiveFormsModule],
  templateUrl: './certificate-select.component.html',
  styleUrl: './certificate-select.component.scss'
})
export class CertificateSelectComponent implements OnInit, OnDestroy {
  @Input({required: true}) items!: BusinessCertificateOptionDto[];

  /** The certificate's production site as one address line, '' when it has none. */
  protected siteText(item: BusinessCertificateOptionDto | null | undefined): string {
    return formatProductionSite(item?.site);
  }

  /** The sub-district (or the next thing that tells sites apart) for the chip. */
  protected siteShort(item: BusinessCertificateOptionDto | null | undefined): string {
    return formatProductionSiteShort(item?.site);
  }
  @Input({required: true}) control!: AbstractControl;
  @Input() size: 'sm' | 'md' = 'md';

  /**
   * Emits the current selection (array of bindValue strings) whenever the
   * control value changes. ng-select writes through [formControl] with
   * emitViewToModelChange=false, so a sibling ngModel on the same control
   * never sees the change — parents that mirror the selection into a model
   * field must listen here.
   */
  @Output() selectionChange = new EventEmitter<string[]>();

  private subscription = new Subscription();
  private lastEmitted: string[] | null = null;

  ngOnInit(): void {
    // control is an @Input, so subscribe in ngOnInit (first change detection).
    // Baseline lastEmitted with the control's current value: ng-select
    // performs an initial (no-op) write of the current value on startup, and
    // without this baseline that write looks like a change and makes the
    // form mark the hidden required field touched while the form is still
    // untouched.
    const initialValue = Array.isArray(this.control.value) ? (this.control.value as string[]) : [];
    this.lastEmitted = [...initialValue];
    this.subscription.add(
      this.control.valueChanges.subscribe((value) => {
        const keys: string[] = Array.isArray(value) ? value : [];
        if (this.lastEmitted === null || keys.length !== this.lastEmitted.length || keys.some((k, i) => k !== this.lastEmitted[i])) {
          this.lastEmitted = keys;
          this.selectionChange.emit(keys);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  @HostBinding('class.cert-select--sm') get isSm(): boolean {
    return this.size === 'sm';
  }

  @HostBinding('class.cert-select--md') get isMd(): boolean {
    return this.size !== 'sm';
  }

  @HostBinding('class.is-invalid') get isInvalid(): boolean {
    return this.control.invalid && (this.control.touched || this.control.dirty);
  }

  get fc(): FormControl {
    return this.control as FormControl;
  }

  get selectedCount(): number {
    const value = this.fc.value;
    return Array.isArray(value) ? value.length : 0;
  }

  getTypeLabel(cert: BusinessCertificateOptionDto): string {
    const normalized = `${cert.certificateType ?? ''}`.trim().toUpperCase();
    if (normalized === 'LEGACY') {
      return 'ดึงมา';
    }
    if (normalized === 'SELF_CREATED' || normalized === 'SELF') {
      return 'สร้างเอง';
    }
    return normalized || 'อื่นๆ';
  }

  isLegacy(cert: BusinessCertificateOptionDto): boolean {
    return `${cert.certificateType ?? ''}`.trim().toUpperCase() === 'LEGACY';
  }

  isSelfCreated(cert: BusinessCertificateOptionDto): boolean {
    const normalized = `${cert.certificateType ?? ''}`.trim().toUpperCase();
    return normalized === 'SELF_CREATED' || normalized === 'SELF';
  }

  isSelected(cert: BusinessCertificateOptionDto): boolean {
    const value = this.fc.value;
    return Array.isArray(value) && value.includes(cert.certificateKey);
  }

  isExpired(cert: BusinessCertificateOptionDto): boolean {
    return cert.status === 'expired';
  }
}
